import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminMerchantsAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import {
  Search, RefreshCw, Pause, Play, KeyRound,
  Eye, ChevronLeft, ChevronRight, X, Loader2, Users, Store,
  SlidersHorizontal, RotateCcw,
} from 'lucide-react';
import ActionMenu from '../../shared/ActionMenu';

const C = {
  bg: '#f4f4f9',
  surface: '#FFFFFF',
  border: '#e8e8f1',
  borderSoft: '#f0f0f6',
  ink: '#14142b',
  body: '#4a4a6a',
  muted: '#6e6e86',
  primary: '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success: '#10B981',
  successSoft: '#D1FAE5',
  warning: '#F59E0B',
  warnSoft: '#FEF3C7',
  danger: '#EF4444',
  dangerSoft: '#FEE2E2',
  radius: 14,
  radiusSm: 8,
};

const fmtINR = (val) => {
  const num = parseFloat(val) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(num);
};

const Badge = ({ color, bg, children }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color,
    background: bg,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const approvalBadge = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'approved':  return <Badge color={C.success} bg={C.successSoft}>Approved</Badge>;
    case 'pending':   return <Badge color={C.warning} bg={C.warnSoft}>Pending</Badge>;
    case 'rejected':  return <Badge color={C.danger}  bg={C.dangerSoft}>Rejected</Badge>;
    case 'suspended': return <Badge color={C.muted}   bg={C.borderSoft}>Suspended</Badge>;
    default:          return <Badge color={C.muted}   bg={C.borderSoft}>{status || '—'}</Badge>;
  }
};

const accountBadge = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active': return <Badge color={C.success} bg={C.successSoft}>Active</Badge>;
    default:       return <Badge color={C.danger}  bg={C.dangerSoft}>{status || 'Inactive'}</Badge>;
  }
};

const KpiCard = ({ label, value, color, bg, Icon, active, onClick }) => {
  const [hov, setHov] = React.useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: active ? color + '12' : '#fff',
        border: `1.5px solid ${active ? color : hov ? color + '55' : '#e8e8f1'}`,
        borderRadius: 14, padding: '18px 20px', cursor: 'pointer', transition: 'all .15s',
        boxShadow: active ? `0 4px 20px ${color}20` : '0 1px 4px rgba(0,0,0,.04)',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: active ? color : '#14142b', lineHeight: 1, minHeight: 30, display: 'flex', alignItems: 'center' }}>{value}</div>
        </div>
        {Icon && <div style={{ width: 38, height: 38, borderRadius: 10, background: active ? color : bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .15s' }}>
          <Icon size={17} color={active ? '#fff' : color} />
        </div>}
      </div>
    </div>
  );
};

const IconBtn = ({ onClick, title, bg, color, children, as: As, to }) => {
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: C.radiusSm,
    border: 'none',
    cursor: 'pointer',
    background: bg,
    color,
    transition: 'opacity .15s',
    textDecoration: 'none',
    flexShrink: 0,
  };
  if (As === Link) {
    return (
      <Link to={to} title={title} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <button onClick={onClick} title={title} style={style}>
      {children}
    </button>
  );
};

export default function AdminMerchants() {
  const navigate = useNavigate();

  const [list, setList] = useState({ data: [], last_page: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [accountStatus, setAccountStatus] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (search) params.search = search;
      if (accountStatus) params.account_status = accountStatus;
      if (approvalStatus) params.status = approvalStatus;
      const { data } = await adminMerchantsAPI.list(params);
      setList(data?.data || { data: [], last_page: 1 });
    } catch (e) {
      toast.error('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, accountStatus, approvalStatus]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  const handleSuspend = async (m) => {
    if (!window.confirm(`Suspend ${m.email}?`)) return;
    await adminMerchantsAPI.suspend(m.id);
    toast.success('Merchant suspended');
    load();
  };

  const handleActivate = async (m) => {
    await adminMerchantsAPI.activate(m.id);
    toast.success('Merchant activated');
    load();
  };

  const handleRotate = async (m) => {
    if (!window.confirm(`Rotate credentials for ${m.email}?`)) return;
    const { data } = await adminMerchantsAPI.rotateCredentials(m.id);
    toast.warn('New secret_salt: ' + data?.data?.secret_salt + ' — copy it now!');
    load();
  };

  const merchants = list.data || [];
  const lastPage = list.last_page || 1;

  const getPaginationPages = () => {
    const pages = [];
    const delta = 2;
    for (let i = Math.max(1, page - delta); i <= Math.min(lastPage, page + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  // ── Styles ──────────────────────────────────────────────────────────────────

  const s = {
    page: {
      minHeight: '100vh',
      background: C.bg,
      padding: '28px 32px',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    },
    headerRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: 24,
      gap: 12,
    },
    h2: {
      margin: 0,
      fontSize: 24,
      fontWeight: 700,
      color: C.ink,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    subtitle: {
      margin: '4px 0 0',
      fontSize: 14,
      color: C.body,
    },
    subtitleLink: {
      color: C.primary,
      textDecoration: 'underline',
      textUnderlineOffset: 2,
    },

    // Filter bar
    filterCard: {
      background: C.surface,
      borderRadius: C.radius,
      border: `1px solid ${C.border}`,
      padding: '14px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginBottom: 20,
      flexWrap: 'wrap',
    },
    searchForm: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 1 220px',
      minWidth: 180,
      position: 'relative',
    },
    searchIcon: {
      position: 'absolute',
      left: 10,
      color: C.muted,
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
    },
    searchInput: {
      width: '100%',
      paddingLeft: 34,
      paddingRight: 12,
      paddingTop: 8,
      paddingBottom: 8,
      border: `1px solid ${C.border}`,
      borderRadius: C.radiusSm,
      fontSize: 14,
      color: C.ink,
      background: C.bg,
      outline: 'none',
      boxSizing: 'border-box',
    },
    select: {
      padding: '8px 12px',
      border: `1px solid ${C.border}`,
      borderRadius: C.radiusSm,
      fontSize: 14,
      color: C.body,
      background: C.surface,
      outline: 'none',
      cursor: 'pointer',
      flex: '0 0 auto',
    },
    refreshBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 14px',
      border: `1px solid ${C.border}`,
      borderRadius: C.radiusSm,
      fontSize: 14,
      fontWeight: 500,
      color: C.body,
      background: C.surface,
      cursor: 'pointer',
      flexShrink: 0,
    },

    // Table card
    tableCard: {
      background: C.surface,
      borderRadius: C.radius,
      border: `1px solid ${C.border}`,
      overflow: 'hidden',
    },
    tableWrap: {
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 14,
    },
    th: {
      padding: '11px 14px',
      textAlign: 'left',
      fontSize: 12,
      fontWeight: 600,
      color: C.muted,
      textTransform: 'uppercase',
      letterSpacing: '.04em',
      background: C.bg,
      borderBottom: `1px solid ${C.border}`,
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '13px 14px',
      color: C.body,
      borderBottom: `1px solid ${C.borderSoft}`,
      verticalAlign: 'middle',
    },
    merchantName: {
      fontWeight: 700,
      color: C.ink,
      display: 'block',
    },
    merchantEmail: {
      fontSize: 12,
      color: C.muted,
      display: 'block',
      marginTop: 1,
    },
    mono: {
      fontFamily: "'Fira Mono', 'Courier New', monospace",
      fontSize: 12,
      color: C.body,
    },
    actionsCell: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },

    // Empty / Loading states
    emptyRow: {
      textAlign: 'center',
      padding: '56px 20px',
      color: C.muted,
      fontSize: 14,
    },
    emptyIcon: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: 12,
    },

    // Pagination
    paginationRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 18px',
      borderTop: `1px solid ${C.borderSoft}`,
      flexWrap: 'wrap',
      gap: 8,
    },
    paginationInfo: {
      fontSize: 13,
      color: C.muted,
    },
    paginationBtns: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },

    // Modal
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(20,20,43,.45)',
      backdropFilter: 'blur(3px)',
      WebkitBackdropFilter: 'blur(3px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    modalCard: {
      background: C.surface,
      borderRadius: C.radius,
      padding: 28,
      width: '100%',
      maxWidth: 380,
      boxShadow: '0 20px 60px rgba(20,20,43,.18)',
      position: 'relative',
    },
    modalTitle: {
      margin: '0 0 4px',
      fontSize: 18,
      fontWeight: 700,
      color: C.ink,
    },
    modalSubtitle: {
      margin: '0 0 20px',
      fontSize: 13,
      color: C.muted,
    },
    modalCloseBtn: {
      position: 'absolute',
      top: 16,
      right: 16,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: C.muted,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 4,
      borderRadius: 6,
    },
    inputRow: {
      display: 'flex',
      alignItems: 'center',
      border: `1px solid ${C.border}`,
      borderRadius: C.radiusSm,
      overflow: 'hidden',
      marginBottom: 20,
    },
    numberInput: {
      flex: 1,
      padding: '10px 12px',
      border: 'none',
      outline: 'none',
      fontSize: 15,
      color: C.ink,
      background: C.surface,
    },
    percentSuffix: {
      padding: '10px 12px',
      background: C.bg,
      borderLeft: `1px solid ${C.border}`,
      fontSize: 14,
      fontWeight: 600,
      color: C.muted,
    },
    modalBtns: {
      display: 'flex',
      gap: 10,
      justifyContent: 'flex-end',
    },
    cancelBtn: {
      padding: '9px 18px',
      border: `1px solid ${C.border}`,
      borderRadius: C.radiusSm,
      background: C.surface,
      color: C.body,
      fontSize: 14,
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveBtn: {
      padding: '9px 20px',
      border: 'none',
      borderRadius: C.radiusSm,
      background: C.primary,
      color: '#fff',
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
    },
  };

  const pageBtn = (active) => ({
    minWidth: 32,
    height: 32,
    padding: '0 6px',
    border: `1px solid ${active ? C.primary : C.border}`,
    borderRadius: C.radiusSm,
    background: active ? C.primary : C.surface,
    color: active ? '#fff' : C.body,
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const pageNavBtn = (disabled) => ({
    width: 32,
    height: 32,
    border: `1px solid ${C.border}`,
    borderRadius: C.radiusSm,
    background: disabled ? C.bg : C.surface,
    color: disabled ? C.muted : C.body,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const mStats = {
    total:     merchants.length,
    approved:  merchants.filter(m => (m.status || '').toLowerCase() === 'approved').length,
    pending:   merchants.filter(m => (m.status || '').toLowerCase() === 'pending').length,
    rejected:  merchants.filter(m => (m.status || '').toLowerCase() === 'rejected').length,
    suspended: merchants.filter(m => (m.account_status || '').toLowerCase() === 'suspended').length,
  };

  const approvalTabs = [
    { label: 'All',       val: '',          color: C.primary, bg: C.primarySoft, Icon: Store  },
    { label: 'Approved',  val: 'approved',  color: C.success, bg: C.successSoft, Icon: Users  },
    { label: 'Pending',   val: 'pending',   color: C.warning, bg: C.warnSoft,    Icon: Loader2 },
    { label: 'Rejected',  val: 'rejected',  color: C.danger,  bg: C.dangerSoft,  Icon: X      },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .mch-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
        @media (max-width: 900px) { .mch-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .mch-kpi-grid { grid-template-columns: 1fr 1fr; } }
      `}</style>

      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, display: 'flex', alignItems: 'center', gap: 9, letterSpacing: '-.02em' }}>
            <Store size={22} color={C.primary} /> Merchants
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>
            Manage merchant accounts —{' '}
            <Link to="/admin/users" style={{ color: C.primary, textDecoration: 'underline' }}>approve pending sign-ups</Link>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setSearch(''); setAccountStatus(''); setApprovalStatus(''); setPage(1); }}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: C.surface, color: C.body, border: `1.5px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <RotateCcw size={13} /> Reset
          </button>
          <button onClick={() => load()}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: C.surface, color: C.body, border: `1.5px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="mch-kpi-grid">
        {approvalTabs.map(k => (
          <KpiCard key={k.val} label={k.label} value={k.val === '' ? list.last_page > 1 ? '—' : mStats.total : mStats[k.val] || 0}
            color={k.color} bg={k.bg} Icon={k.Icon}
            active={approvalStatus === k.val}
            onClick={() => { setApprovalStatus(k.val); setPage(1); }} />
        ))}
      </div>

      {/* ── Filter Bar ──────────────────────────────────────────────── */}
      <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: '10px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <form onSubmit={onSearch} style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search by name, email, MID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', height: 38, padding: '0 12px 0 34px', fontSize: 13, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, outline: 'none', color: C.ink, boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        </form>
        <select value={accountStatus} onChange={e => { setAccountStatus(e.target.value); setPage(1); }}
          style={{ height: 38, padding: '0 12px', fontSize: 13, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, outline: 'none', color: C.ink, background: C.surface, fontFamily: 'inherit' }}>
          <option value="">All Account Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* ── Table Card ──────────────────────────────────────────────── */}
      <div style={s.tableCard}>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Merchant', 'MID', 'Approval', 'Account', 'Wallet', 'Credentials', 'Actions'].map((col) => (
                  <th key={col} style={s.th}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={s.emptyRow}>
                    <div style={s.emptyIcon}>
                      <Loader2 size={28} color={C.muted} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                    Loading merchants…
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </td>
                </tr>
              ) : merchants.length === 0 ? (
                <tr>
                  <td colSpan={8} style={s.emptyRow}>
                    <div style={s.emptyIcon}><Users size={32} color={C.muted} /></div>
                    No merchants found.
                  </td>
                </tr>
              ) : merchants.map((m) => (
                <tr
                  key={m.id}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                >
                  {/* Merchant */}
                  <td style={s.td}>
                    <span style={s.merchantName}>{m.username || m.name || '—'}</span>
                    <span style={s.merchantEmail}>{m.email}</span>
                    {m.phone && <span style={{ ...s.merchantEmail, marginTop: 1 }}>{m.phone}</span>}
                  </td>

                  {/* MID */}
                  <td style={s.td}>
                    <span style={s.mono}>{m.mid || '—'}</span>
                  </td>

                  {/* Approval */}
                  <td style={s.td}>{approvalBadge(m.status)}</td>

                  {/* Account */}
                  <td style={s.td}>{accountBadge(m.account_status)}</td>

                  {/* Wallet */}
                  <td style={s.td}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtINR(m.wallet_balance)}</span>
                  </td>

                  {/* Credentials */}
                  <td style={s.td}>
                    {m.api_key_issued_at
                      ? <Badge color={C.success} bg={C.successSoft}>&#10003; Issued</Badge>
                      : <span style={{ color: C.muted }}>—</span>
                    }
                  </td>

                  {/* Actions */}
                  <td style={s.td}>
                    <ActionMenu items={[
                      { label: 'View Details', Icon: Eye, color: C.primary, bg: C.primarySoft, onClick: () => navigate(`/admin/merchants/${m.id}`) },
                      { divider: true },
                      (m.account_status || '').toLowerCase() === 'active'
                        ? { label: 'Suspend', Icon: Pause, color: C.danger, bg: C.dangerSoft, onClick: () => handleSuspend(m) }
                        : { label: 'Activate', Icon: Play, color: C.success, bg: C.successSoft, onClick: () => handleActivate(m) },
                      { label: 'Rotate Credentials', Icon: KeyRound, color: C.warning, bg: C.warnSoft, onClick: () => handleRotate(m) },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ────────────────────────────────────────────── */}
        {!loading && merchants.length > 0 && (
          <div style={s.paginationRow}>
            <span style={s.paginationInfo}>
              Page {page} of {lastPage}
            </span>
            <div style={s.paginationBtns}>
              <button
                style={pageNavBtn(page === 1)}
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={15} />
              </button>

              {getPaginationPages().map((p) => (
                <button
                  key={p}
                  style={pageBtn(p === page)}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}

              <button
                style={pageNavBtn(page === lastPage)}
                disabled={page === lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
